import { createContext, useContext } from 'react';
import { ApiError, Me } from '../lib/api';
import { t } from '../lib/i18n';
import { errorText } from '../pages/Contacts';
import type { Outgoing } from './Composer';
import type { Approval, PilotSettings, PilotStatus, PlanRow, Revision, Run, RunDetail } from './model';

export interface PlanDetail {
  plan: PlanRow;
  revision: Revision | null;
  approvals: Approval[];
  run: Run | null;
}

/** What every card of a thread needs: who is looking, the live plan, and how to act (SPEC §8.1). */
export interface PilotCtxValue {
  me: Me;
  writer: boolean;
  owner: boolean;
  status: PilotStatus | null;
  settings: PilotSettings | null;
  plan: PlanRow | null;
  detail: PlanDetail | null;
  runDetail: RunDetail | null;
  busy: boolean;
  revision: (planId: string, r: number) => Promise<Revision>;
  reload: () => Promise<void>;
  send: (m: Outgoing & { answer_to?: { field: string; value_json: string; remember?: boolean } }) => Promise<boolean>;
}

export const PilotCtx = createContext<PilotCtxValue | null>(null);

export function usePilot(): PilotCtxValue {
  const v = useContext(PilotCtx);
  if (!v) throw new Error('PilotCtx missing');
  return v;
}

const CODES: Record<string, string> = {
  APPROVAL_SELF: 'Вы автор этой версии: согласовать её должен другой владелец или администратор пространства.',
  APPROVAL_ECHO_MISMATCH: 'Значения на карточке устарели или число получателей набрано неверно. Обновите карточку и сверьте числа.',
  APPROVAL_HASH_MISMATCH: 'Вы видели другую версию плана. Обновите поток.',
  REVISION_CONFLICT: 'План изменился, пока вы смотрели. Посмотрите последнюю версию.',
  APPROVAL_NOT_PENDING: 'Это согласование уже не ждёт решения.',
  APPROVAL_EXPIRED: 'Срок согласования истёк — запросите новое.',
  APPROVAL_ALREADY_OPEN: 'Согласование уже запрошено.',
  APPROVER_ROLE_LOST: 'Согласовавший больше не владелец или администратор — нужно новое согласование.',
  PLAN_NOT_APPROVED: 'У этой версии нет действующего согласования.',
  PLAN_WINDOW_MISSED: 'Окно отправки пропущено: перенесите время в плане и согласуйте заново.',
  PLAN_CONTENT_DRIFT: 'Отрисованное письмо отличается от согласованного — запуск остановлен. Создайте новую версию.',
  PLAN_LOCKED: 'План уже запущен или закрыт — изменить его нельзя.',
  PILOT_LOCKED_BY_STAFF: 'Запуск остановлен MailOcean — возобновить его может только поддержка.',
  RUN_NOT_ACTIVE: 'Запуск сейчас в другом состоянии. Обновите карточку.',
  PILOT_RATE_LIMITED: 'Слишком много команд подряд — подождите немного.',
  PILOT_DISABLED: 'Пилот выключен на этой установке.',
  DOMAIN_NOT_VERIFIED: 'Домен отправителя не подтверждён.',
  SENDING_SUSPENDED: 'Отправка заблокирована MailOcean.',
};

/** A Pilot error in words: known codes in plain text, otherwise the server's detail. */
export function pilotError(err: unknown): string {
  if (err instanceof ApiError && CODES[err.code]) {
    const d = err.details?.[0];
    return d && err.code !== 'APPROVAL_SELF' ? `${t(CODES[err.code])} (${d.message})` : t(CODES[err.code]);
  }
  return errorText(err);
}
